import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import CIcon from "@coreui/icons-react";
import { Tooltip } from 'antd';
import DatePicker, { utils } from '@hassanmojab/react-modern-calendar-datepicker';
import moment from "moment";
import * as icon from '@coreui/icons';

import FilterChip from './FilterChip';
import { DateRangeSelect, FilterType } from "../_lib/constants";

import '@hassanmojab/react-modern-calendar-datepicker/lib/DatePicker.css';

interface OfferAnalyticCardProps {
    amount_eur?: string;
    amount_chf?: string;
    amount_gbp?: string;
    amount_sek?: string;
    amount_ron?: string;
    amount_pln?: string;
    amount_czk?: string;
    amount_huf?: string;
    isLoading?: boolean;
    count?: number;
    title?: string;
    onDateRangeChange?: Function;
    onTodayClick?: Function;
    onTriggerReset?: Function;
    isGlobalToday?: boolean;
    highestOffer?: string | number;
    currencyTitle?: string;
    hideBadge?: boolean;
    ref?: any;
}

const OfferAnalyticCard: React.FC<OfferAnalyticCardProps> = forwardRef(({
    amount_chf,
    amount_eur,
    amount_gbp,
    amount_sek,
    amount_ron,
    amount_pln,
    amount_czk,
    amount_huf,
    isLoading = false,
    count = 0,
    title = '',
    onDateRangeChange,
    onTodayClick,
    onTriggerReset,
    isGlobalToday = false,
    highestOffer,
    currencyTitle,
    hideBadge = false
}, ref) => {
    const [filterChip, setFilterChip] = useState<string>(FilterType?.total);
    const [dateRange, setDateRange] = useState<Record<string, any>>({
        from: '',
        to: ''
    });
    const [selectedDayRange, setSelectedDayRange] = useState<DateRangeSelect>({
        from: null,
        to: null
    });
    const [isGlobalFilter, setIsGlobalFilter] = useState<boolean>(isGlobalToday);

    const handleTodayClick = (type: string): void => {
        setFilterChip(type);
        setSelectedDayRange({
            from: null,
            to: null
        });
        onTodayClick?.();
    };

    const handleDateFilter = (dateRange: DateRangeSelect): void => {
        setSelectedDayRange(dateRange);
        const { from, to } = dateRange;
        const fromDate = from ? `${from?.day}/${from?.month}/${from?.year}` : null;
        const toDate = to ? `${to?.day}/${to?.month}/${to?.year}` : null;
        if (fromDate && toDate) {
            setDateRange({
                from: fromDate,
                to: toDate
            });
            setFilterChip(FilterType?.dateRange);
            onDateRangeChange?.({
                from: moment.utc(fromDate, 'D/M/YYYY').toISOString(),
                to: moment.utc(toDate, 'D/M/YYYY').toISOString()
            });
            setIsGlobalFilter(false);
        }
    };

    const triggerReset = (): void => {
        setFilterChip(FilterType?.total);
        setSelectedDayRange({
            from: null,
            to: null
        });
        if (onTriggerReset) onTriggerReset()
    };

    useImperativeHandle(ref, () => ({
        resetFilter: () => {
            setFilterChip(FilterType?.total);
            setSelectedDayRange({
                from: null,
                to: null
            });
            // if (onTriggerReset) onTriggerReset();
        }
    }));

    useEffect(() => {
        if (isGlobalToday) {
            setFilterChip(FilterType?.today);
        } else {
            setFilterChip(FilterType?.total);
        }
        setIsGlobalFilter(isGlobalToday);
    }, [isGlobalToday]);

    const renderCustomInput = ({ ref }: any) => (
        <>
            <Tooltip placement='top' title="">
                <input
                    readOnly
                    ref={ref}
                    className={`datepicker-input ${isGlobalToday ? 'gloabl-inp' : ''}`}
                    title="Date Range"
                    disabled={isGlobalToday}
                />
            </Tooltip>

        </>
    )

    return (
        <div className='analytics-card mb-lg-0 mb-md-2 mb-2'>
            <div className={`loader-component ${isLoading ? 'loading' : ''}`}>
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
            <div className={`card-inner ${isLoading ? 'loading' : ''}`}>
                <div className='card-head'>
                    {!hideBadge && (
                        <FilterChip
                            filterKey={filterChip !== FilterType?.dateRange ? filterChip : `${dateRange?.from} - ${dateRange.to}`}
                            isGlobal={isGlobalToday}
                        />
                    )}
                    <div className='card-action-row' style={{
                        flexGrow: hideBadge ? 1 : 0
                    }}>
                        <button
                            className={`filter-button ${filterChip === FilterType.today && !isGlobalToday ? 'active-btn' : ''} ${isGlobalToday ? 'global-active' : ''}`}
                            onClick={handleTodayClick.bind(this, FilterType.today)}
                            disabled={isGlobalToday}
                            title="Today"
                        >
                            <CIcon icon='cilCalendarCheck' />
                        </button>
                        <div>
                        <button
                            className={`filter-button ${filterChip === FilterType.dateRange && !isGlobalToday ? 'active-btn' : ''} ${isGlobalToday ? 'global-active' : ''}`}
                            title="Date Range"
                            disabled={isGlobalToday}
                        >
                            <CIcon icon='cilCalendar' />
                        </button>
                        <DatePicker
                            value={selectedDayRange}
                            onChange={handleDateFilter}
                            shouldHighlightWeekends
                            maximumDate={utils('en').getToday()}
                            renderInput={renderCustomInput}
                        />
                        </div>
                        {filterChip !== FilterType?.total && !isGlobalFilter && (
                            <button
                                className='filter-button'
                                onClick={triggerReset}
                                title="Reset"
                            >
                                <CIcon icon={icon?.cilReload} />
                            </button>
                        )}
                    </div>
                </div>
                <div className='card-content'>
                    <div className='count-content'>
                        <h4>{count}</h4>
                        <h6>{title}</h6>
                    </div>
                    {(amount_eur || amount_chf || amount_gbp || amount_sek || amount_ron || amount_pln || amount_czk || amount_huf || highestOffer) && (
                        <div className='right-component'>
                            {currencyTitle && <div className="highest-offer-label">{currencyTitle}</div>}
                            {highestOffer ? (
                                <div className="highest-offer">
                                    <div className="highest-offer-label">Highest offer</div>
                                    <div className="highest-offer-value">{highestOffer}</div>
                                </div>
                            ) : (
                                <ul className="currency-li">
                                    {amount_eur && parseFloat(amount_eur) > 0 && <li>EUR {amount_eur}</li>}
                                    {amount_chf && parseFloat(amount_chf) > 0 && <li>CHF {amount_chf}</li>}
                                    {amount_gbp && parseFloat(amount_gbp) > 0 && <li>GBP {amount_gbp}</li>}
                                    {amount_sek && parseFloat(amount_sek) > 0 && <li>SEK {amount_sek}</li>}
                                    {amount_ron && parseFloat(amount_ron) > 0 && <li>RON {amount_ron}</li>}
                                    {amount_pln && parseFloat(amount_pln) > 0 && <li>PLN {amount_pln}</li>}
                                    {amount_czk && parseFloat(amount_czk) > 0 && <li>CZK {amount_czk}</li>}
                                    {amount_huf && parseFloat(amount_huf) > 0 && <li>HUF {amount_huf}</li>}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
});

export default OfferAnalyticCard;